import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useLang } from '@/context/LangContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Check, ArrowRight, ArrowLeft, Zap, Loader2, Link2, Sparkles } from 'lucide-react';
import { BrandAuditStep } from '@/components/BrandAuditStep';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OnboardingPage() {
  const { user, refreshUser } = useAuth();
  const { isDark } = useTheme();
  const { t } = useLang();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [channels, setChannels] = useState([]);
  const [connected, setConnected] = useState([]);
  const [connecting, setConnecting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState({ social_urls: {} });
  const [savedAudit, setSavedAudit] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/channels/available`).then(r => setChannels(r.data)).catch(e => console.error('Failed to load channels:', e));
  }, []);

  useEffect(() => {
    if (user?.connected_channels) setConnected(user.connected_channels.map(c => c.channel_id));
  }, [user]);

  const connectChannel = useCallback(async (ch) => {
    setConnecting(ch.id);
    // Optimistic demo flow — show success toast even when backend is unavailable.
    let backendOk = false;
    try {
      await axios.post(`${API}/api/channels/connect`, {
        channel_id: ch.id, channel_name: ch.name, channel_type: ch.type
      }, { withCredentials: true });
      backendOk = true;
    } catch (e) {
      console.warn('Channel connect API unavailable, using demo mode:', e?.message || e);
    }
    setConnected(prev => prev.includes(ch.id) ? prev : [...prev, ch.id]);
    setConnecting(null);
    toast.success(`${ch.name} ${t('dash.connect_success').toLowerCase()}`, {
      description: backendOk ? 'Data will sync within 60 seconds.' : 'Demo connection — sample data is live in your dashboard.',
    });
  }, [t]);

  // The audit is submitted when the user leaves the audit step, so the
  // business model is known before we show them a dashboard.
  const submitAudit = useCallback(async () => {
    const num = v => (v === '' || v === null || v === undefined ? null : Number(v));
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/onboarding/audit`, {
        brand_name: audit.brand_name, website_url: audit.website_url,
        social_urls: Object.fromEntries(Object.entries(audit.social_urls || {}).filter(([, v]) => v)),
        current_mrr: num(audit.current_mrr), current_roas: num(audit.current_roas),
        target_budget: num(audit.target_budget), north_star: audit.north_star,
        business_model: audit.business_model || null, org_name: audit.org_name || null,
        referral_code: localStorage.getItem('omni-ref') || null,
      }, { withCredentials: true });
      setSavedAudit(data);
      localStorage.removeItem('omni-ref');
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save your audit. Please check the required fields.');
    } finally {
      setBusy(false);
    }
  }, [audit]);

  const finishOnboarding = useCallback(async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/onboarding/complete`, {}, { withCredentials: true });
      await refreshUser();
    } catch (e) {
      console.error('Failed to complete onboarding:', e);
    }
    setBusy(false);
    navigate('/dashboard');
  }, [refreshUser, navigate]);

  const steps = [
    { title: 'Welcome to OmniView', sub: 'Let\'s set up your command center in under 2 minutes.' },
    { title: 'Your Brand Audit', sub: 'This shapes every metric we show you. Two minutes, and it is worth it.' },
    { title: 'Connect Your Channels', sub: 'Select the platforms you use. You can add more later.' },
    { title: 'You\'re All Set!', sub: 'Your dashboard is ready. Let\'s dive in.' },
  ];
  const auditReady = Boolean((audit.brand_name || '').trim() && (audit.website_url || '').trim() && audit.north_star);

  const categories = [
    { key: 'paid', label: 'Paid Media', color: '#4285F4' },
    { key: 'analytics', label: 'Analytics', color: '#E8710A' },
    { key: 'seo', label: 'SEO', color: '#34A853' },
    { key: 'ecom', label: 'E-Commerce', color: '#FF9900' },
    { key: 'crm', label: 'CRM', color: '#FF7A59' },
    { key: 'qcom', label: 'Q-Commerce', color: '#8B5CF6' },
    { key: 'marketplace', label: 'Marketplace', color: '#FC2779' },
    { key: 'data', label: 'Data Source', color: '#0F9D58' },
  ];

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 py-12 ${isDark ? 'bg-zinc-950' : 'bg-zinc-50'}`}
      style={{ fontFamily: "'Outfit', sans-serif" }}>
      <div className={`w-full max-w-2xl rounded-2xl overflow-hidden ${isDark ? 'bg-zinc-900 border border-zinc-800' : 'bg-white border border-zinc-200 shadow-xl'}`}>
        {/* Progress */}
        <div className={`px-8 pt-6 pb-4 border-b ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-xs"
              style={{ background: 'linear-gradient(135deg,#002FA7,#00D4FF)' }}>{'\u03A9'}</div>
            <span className="font-bold">OmniView Setup</span>
          </div>
          <div className="flex gap-2">
            {steps.map((_, i) => (
              <div key={`step-bar-${i}`} className="flex-1 h-1.5 rounded-full transition-all"
                style={{ background: i <= step ? '#00D4FF' : (isDark ? '#27272a' : '#e4e4e7') }} />
            ))}
          </div>
          <p className={`text-xs mt-2 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Step {step + 1} of {steps.length}</p>
        </div>

        <div className="p-8">
          {/* Step 0: Welcome */}
          {step === 0 && (
            <div className="text-center py-8" data-testid="onboarding-welcome">
              <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg,#002FA7,#00D4FF)' }}>
                <Zap size={28} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold mb-2">{steps[0].title}</h2>
              <p className={`text-base mb-2 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{steps[0].sub}</p>
              <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
                Hi{user?.name ? `, ${user.name}` : ''}! Your 7-day free trial has started. Connect at least one channel to unlock your dashboard.
              </p>
            </div>
          )}

          {/* Step 1: Brand audit */}
          {step === 1 && (
            <div>
              <h2 className="text-xl font-bold mb-1">{steps[1].title}</h2>
              <p className={`text-sm mb-5 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{steps[1].sub}</p>
              <BrandAuditStep isDark={isDark} value={audit} onChange={setAudit} />
            </div>
          )}

          {/* Step 2: Channels */}
          {step === 2 && (
            <div data-testid="onboarding-channels">
              <h2 className="text-xl font-bold mb-1">{steps[2].title}</h2>
              <p className={`text-sm mb-6 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                {steps[2].sub} <span style={{ color: '#06D6A0' }}>{connected.length} connected</span>
              </p>
              <div className="space-y-5 max-h-[400px] overflow-y-auto pr-2">
                {categories.map(cat => {
                  const catChannels = channels.filter(c => c.type === cat.key);
                  if (!catChannels.length) return null;
                  return (
                    <div key={cat.key}>
                      <div className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}
                        style={{ color: cat.color }}>{cat.label}</div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {catChannels.map(ch => {
                          const isConnected = connected.includes(ch.id);
                          const isConnecting = connecting === ch.id;
                          return (
                            <button key={ch.id} data-testid={`channel-${ch.id}`}
                              onClick={() => !isConnected && connectChannel(ch)}
                              disabled={isConnected || isConnecting}
                              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-all text-sm ${
                                isConnected
                                  ? (isDark ? 'border-green-800 bg-green-900/20' : 'border-green-300 bg-green-50')
                                  : (isDark ? 'border-zinc-700 hover:border-zinc-500 bg-zinc-800' : 'border-zinc-200 hover:border-zinc-400 bg-white')
                              }`}>
                              <div className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                                style={{ background: ch.color }}>{ch.icon?.charAt(0)}</div>
                              <span className={`flex-1 font-medium truncate ${isDark ? 'text-zinc-200' : 'text-zinc-700'}`}>{ch.name}</span>
                              {isConnecting && <Loader2 size={14} className="animate-spin text-blue-500"/>}
                              {isConnected && <Check size={14} className="text-green-500 flex-shrink-0"/>}
                              {!isConnected && !isConnecting && <Link2 size={14} className={isDark ? 'text-zinc-600' : 'text-zinc-400'}/>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Done */}
          {step === 3 && (
            <div className="text-center py-8" data-testid="onboarding-done">
              <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center" style={{ background: '#06D6A0' }}>
                <Sparkles size={28} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold mb-2">{steps[3].title}</h2>
              <p className={`text-base mb-4 ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{steps[3].sub}</p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${isDark ? 'bg-zinc-800 text-zinc-300' : 'bg-zinc-100 text-zinc-700'}`}>
                  <Check size={14} style={{ color: '#06D6A0' }}/> {connected.length} channels connected
                </span>
                {savedAudit?.kpi_schema && (
                  <span data-testid="onboarding-model-summary"
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${isDark ? 'bg-zinc-800 text-zinc-300' : 'bg-zinc-100 text-zinc-700'}`}>
                    <Check size={14} style={{ color: '#06D6A0' }}/>
                    {savedAudit.kpi_schema.business_model === 'b2b_leadgen' ? 'Lead gen metrics'
                      : savedAudit.kpi_schema.business_model === 'hybrid' ? 'Hybrid metrics' : 'E-commerce metrics'} enabled
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-8 py-5 border-t flex items-center justify-between ${isDark ? 'border-zinc-800' : 'border-zinc-200'}`}>
          {step > 0 ? (
            <button data-testid="onboarding-prev" onClick={() => setStep(step - 1)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${isDark ? 'text-zinc-400 hover:text-zinc-200' : 'text-zinc-500 hover:text-zinc-700'}`}>
              <ArrowLeft size={16}/> Back
            </button>
          ) : <div/>}
          {step < 3 ? (
            <button data-testid="onboarding-next" disabled={busy || (step === 1 && !auditReady)}
              onClick={() => (step === 1 ? submitAudit() : setStep(step + 1))}
              className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg,#002FA7,#00D4FF)' }}>
              {busy && <Loader2 size={16} className="animate-spin"/>}
              {step === 0 ? 'Get Started' : step === 1 ? 'Save & Continue' : `Continue (${connected.length} connected)`}
              <ArrowRight size={16}/>
            </button>
          ) : (
            <button data-testid="onboarding-finish" onClick={finishOnboarding} disabled={busy}
              className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50"
              style={{ background: '#06D6A0' }}>
              {busy ? <Loader2 size={16} className="animate-spin"/> : null}
              Launch Dashboard <ArrowRight size={16}/>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
